import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function ScenarioBar({ scenarios, activeId, onSelect, onSnapshot, onDuplicate, onRename, onDelete, onReset, resetDisabled, periodLabel }) {
  const { t } = useTranslation();
  const active = scenarios.find((s) => s.id === activeId);
  const isLocked = !!active?.locked;
  // Un instantané est figé (type 'snapshot' / verrouillé) → lecture seule.
  const isSnapshot = active?.type === 'snapshot' || isLocked;
  // Plans de travail = tout sauf instantanés. On garantit qu'il en reste ≥1 :
  // le dernier plan joue le rôle de base protégée (non supprimable).
  const workingCount = scenarios.filter((s) => s.type !== 'snapshot').length;
  const canDelete = !!active && (isSnapshot || workingCount > 1);
  const showSelect = scenarios.length > 1;

  // Renommage inline du scénario actif (interdit sur un instantané figé).
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  useEffect(() => { setEditing(false); }, [activeId]);

  const startRename = () => { setDraft(active?.name || ''); setEditing(true); };
  const commitRename = () => {
    const name = draft.trim();
    if (name && name !== active?.name) onRename && onRename(activeId, name);
    setEditing(false);
  };

  return (
    <div className="tres-bar">
      <div className="tres-bar__row">
        {/* Sélecteur visible seulement s'il existe plusieurs plans/instantanés ;
            sinon le plan de référence est implicite. Plans de travail en tête. */}
        {showSelect && !editing && (
          <select
            className="tres-bar__select"
            value={activeId || ''}
            onChange={(e) => onSelect(e.target.value)}
            aria-label={t('scenarii:treasury.scenarioBar.selectAria')}
          >
            {[...scenarios]
              .sort((a, b) => {
                const as = a.type === 'snapshot' ? 1 : 0;
                const bs = b.type === 'snapshot' ? 1 : 0;
                return as - bs;
              })
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.type === 'snapshot' ? `📌 ${s.name}` : s.name}
                </option>
              ))}
          </select>
        )}

        {/* Renommage inline (plans de travail uniquement). */}
        {editing && (
          <input
            className="tres-bar__name-input"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              if (e.key === 'Escape') setEditing(false);
            }}
            aria-label={t('scenarii:treasury.scenarioBar.renameAria')}
          />
        )}
        {!editing && active && !isSnapshot && onRename && (
          <button
            type="button"
            className="tres-bar__btn tres-bar__btn--icon"
            onClick={startRename}
            title={t('scenarii:treasury.scenarioBar.renameTitle')}
          >
            ✎
          </button>
        )}

        {isSnapshot && active && (
          <span className="tres-bar__locked" title={t('scenarii:treasury.scenarioBar.snapshotLockedTitle')}>
            {t('scenarii:treasury.scenarioBar.snapshotLocked')}
          </span>
        )}

        {/* Période du plan — texte non modifiable (édité dans « Set up »). */}
        {periodLabel && (
          <span className="tres-bar__period" title={t('scenarii:treasury.scenarioBar.periodTitle')}>
            {periodLabel}
          </span>
        )}

        <div className="tres-bar__spacer" />

        {onDuplicate && (
          <button
            className="tres-bar__btn tres-bar__btn--primary"
            onClick={onDuplicate}
            title={t('scenarii:treasury.scenarioBar.newScenarioTitle')}
          >
            {t('scenarii:treasury.scenarioBar.newScenario')}
          </button>
        )}
        <button
          className="tres-bar__btn"
          onClick={onSnapshot}
          title={t('scenarii:treasury.scenarioBar.saveSnapshotTitle')}
        >
          {t('scenarii:treasury.scenarioBar.saveSnapshot')}
        </button>
        <button
          className="tres-bar__btn tres-bar__btn--danger"
          onClick={onReset}
          disabled={resetDisabled}
          title={t('scenarii:treasury.scenarioBar.reinitializeTitle')}
        >
          {t('scenarii:treasury.scenarioBar.reinitialize')}
        </button>
        {/* Le dernier plan de travail (base) n'est jamais supprimable. */}
        {canDelete && (
          <button className="tres-bar__btn tres-bar__btn--danger" onClick={onDelete}>
            {isSnapshot ? t('scenarii:treasury.scenarioBar.deleteSnapshot') : t('scenarii:treasury.scenarioBar.deletePlan')}
          </button>
        )}
      </div>
    </div>
  );
}
